import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Star, ShieldCheck, Anchor, MapPin, Heart, Calendar, Users, PlusCircle,
  CheckCircle2, XCircle, Clock, Inbox, MessageCircle, Send, ArrowLeft, Slash, LogOut,
} from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import TripCard from "../components/TripCard";
import CaptainTripCard from "../components/CaptainTripCard";
import Avatar from "../components/Avatar";
import ProfilePhotoUploader from "../components/ProfilePhotoUploader";
import FounderBadge from "../components/FounderBadge";

const BOOKING_LABEL = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
  cancelled: "Annulée",
  confirmé: "Confirmée",
};

export default function MonEspace() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "bookings";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => { document.title = "Mon espace — Le Bon Matelot"; }, []);
  useEffect(() => {
    if (!loading && !user) nav("/connexion?next=/mon-espace");
  }, [loading, user, nav]);
  useEffect(() => {
    setParams({ tab }, { replace: true });
  }, [tab, setParams]);

  const { data: bookings = [] } = useQuery({
    queryKey: ["my-bookings"], enabled: !!user,
    queryFn: async () => (await api.get("/me/bookings")).data,
  });
  const { data: myTrips = [] } = useQuery({
    queryKey: ["my-trips"], enabled: !!user,
    queryFn: async () => (await api.get("/me/trips")).data,
  });
  const { data: requests = [] } = useQuery({
    queryKey: ["my-requests"], enabled: !!user,
    queryFn: async () => (await api.get("/me/booking-requests")).data,
  });
  const { data: favs = [] } = useQuery({
    queryKey: ["my-favs"], enabled: !!user,
    queryFn: async () => (await api.get("/me/favorites")).data,
  });
  const { data: conversations = [] } = useQuery({
    queryKey: ["my-conversations"], enabled: !!user,
    queryFn: async () => (await api.get("/me/conversations")).data,
    refetchInterval: 30000,
  });

  const pendingRequestsCount = requests.filter((r) => r.status === "pending").length;
  const unreadMessages = conversations.reduce((s, c) => s + (c.unread || 0), 0);

  if (loading || !user) {
    return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-bretagne">Chargement…</div>;
  }

  return (
    <div className="bg-blanc">
      {/* Header profile */}
      <div className="relative bg-gradient-to-br from-ocean to-bretagne text-blanc overflow-hidden">
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-corail/30 blur-3xl" />
        <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full bg-turquoise/30 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 py-14 flex flex-col md:flex-row items-start md:items-center gap-6">
          <ProfilePhotoUploader />
          <div className="flex-1 w-full">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-jaune font-bold">Mon espace</div>
                <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{user.name}</h1>
              </div>
              <button
                onClick={async () => { await logout(); nav("/"); toast.success("À bientôt sur Le Bon Matelot !"); }}
                data-testid="logout-btn"
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-blanc/15 hover:bg-blanc/25 text-blanc px-3 py-1.5 rounded-full tactile shrink-0"
                title="Se déconnecter"
              >
                <LogOut className="w-3.5 h-3.5" /> Déconnexion
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {user.home_port || "Port à définir"}</span>
              {user.rating > 0 && (
                <span className="inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5 fill-jaune text-jaune" /> {user.rating.toFixed(1)}</span>
              )}
              <span className="inline-flex items-center gap-1.5"><Anchor className="w-3.5 h-3.5" /> {user.trips_count || 0} sortie{(user.trips_count || 0) > 1 ? "s" : ""}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <FounderBadge show={user.is_founder} variant="solid" />
              {user.verified_email && (
                <span className="inline-flex items-center gap-1 bg-blanc/15 backdrop-blur text-blanc text-xs font-bold px-2.5 py-1 rounded-full">
                  <ShieldCheck className="w-3 h-3" /> Email vérifié
                </span>
              )}
              {user.verified_captain && (
                <span className="inline-flex items-center gap-1 bg-corail text-blanc text-xs font-bold px-2.5 py-1 rounded-full">
                  <Anchor className="w-3 h-3" /> Capitaine vérifié
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-brume p-1 rounded-full flex-wrap h-auto">
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-full data-[state=active]:bg-corail data-[state=active]:text-blanc px-4 sm:px-5">
              Mes demandes ({bookings.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-full data-[state=active]:bg-corail data-[state=active]:text-blanc px-4 sm:px-5">
              Demandes reçues{pendingRequestsCount > 0 && (
                <span className="ml-1.5 bg-corail data-[state=active]:bg-blanc text-blanc data-[state=active]:text-corail text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {pendingRequestsCount}
                </span>
              )} ({requests.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-trips" value="trips" className="rounded-full data-[state=active]:bg-corail data-[state=active]:text-blanc px-4 sm:px-5">
              Mes sorties ({myTrips.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages" className="rounded-full data-[state=active]:bg-corail data-[state=active]:text-blanc px-4 sm:px-5">
              Messages{unreadMessages > 0 && (
                <span className="ml-1.5 bg-corail data-[state=active]:bg-blanc text-blanc data-[state=active]:text-corail text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {unreadMessages}
                </span>
              )} ({conversations.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-favorites" value="favorites" className="rounded-full data-[state=active]:bg-corail data-[state=active]:text-blanc px-4 sm:px-5">
              Favoris ({favs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            {bookings.length === 0 ? (
              <EmptyState text="Pas encore de demande envoyée." cta="Explorer les sorties" onClick={() => nav("/explorer")} />
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {bookings.map((b) => b.trip && (<MyBookingCard key={b.booking_id} booking={b} />))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests" className="mt-8">
            <CaptainRequestsManager requests={requests} />
          </TabsContent>

          <TabsContent value="trips" className="mt-8">
            <CaptainTripsManager trips={myTrips} onPublish={() => nav("/publier")} />
          </TabsContent>

          <TabsContent value="messages" className="mt-8">
            <ConversationsView conversations={conversations} />
          </TabsContent>

          <TabsContent value="favorites" className="mt-8">
            {favs.length === 0 ? (
              <EmptyState text="Aucun favori pour l'instant." cta="Explorer" onClick={() => nav("/explorer")} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {favs.map((t) => (<TripCard key={t.trip_id} trip={t} />))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function BookingStatusPill({ status }) {
  const conf = {
    pending: { bg: "bg-jaune/40", fg: "text-ocean", icon: Clock, label: "Demande envoyée" },
    confirmed: { bg: "bg-turquoise/20", fg: "text-turquoise", icon: CheckCircle2, label: "Acceptée" },
    refused: { bg: "bg-corail/15", fg: "text-corail", icon: XCircle, label: "Refusée" },
    cancelled: { bg: "bg-brume", fg: "text-bretagne", icon: Slash, label: "Annulée par le capitaine" },
    cancelled_by_user: { bg: "bg-brume", fg: "text-bretagne", icon: Slash, label: "Annulée" },
  };
  const c = conf[status] || conf.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 ${c.bg} ${c.fg} text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full w-fit`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function CaptainRequestsManager({ requests }) {
  const [filter, setFilter] = useState("pending");
  const filters = [
    { key: "pending", label: "En attente" },
    { key: "confirmed", label: "Acceptées" },
    { key: "refused", label: "Refusées" },
    { key: "cancelled_by_user", label: "Annulées" },
  ];
  const list = requests.filter((r) => r.status === filter);

  if (requests.length === 0) {
    return (
      <div className="text-center py-16 bg-sable rounded-3xl border border-brume">
        <Inbox className="w-10 h-10 text-ocean mx-auto" />
        <p className="mt-3 text-ocean/80">Aucune demande reçue pour le moment.</p>
        <p className="text-xs text-bretagne mt-1">Vous serez averti·e par email à chaque nouvelle demande.</p>
      </div>
    );
  }

  return (
    <div data-testid="requests-manager">
      <div className="flex items-center gap-1.5 bg-brume/60 p-1 rounded-full w-fit mb-5">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`req-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs md:text-sm font-bold px-3 md:px-4 py-1.5 rounded-full transition ${
              filter === f.key ? "bg-ocean text-blanc" : "text-ocean/70 hover:text-ocean"
            }`}
          >
            {f.label} ({requests.filter((r) => r.status === f.key).length})
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="text-center py-10 bg-sable rounded-2xl border border-brume text-sm text-ocean/70">
          Aucune demande dans cette catégorie.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (<RequestCard key={r.booking_id} req={r} />))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ req }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [showReply, setShowReply] = useState(false);

  const respond = async (action) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${req.booking_id}/respond`, { action, reply });
      toast.success(action === "accept" ? "Demande acceptée" : "Demande refusée");
      qc.invalidateQueries({ queryKey: ["my-requests"] });
      qc.invalidateQueries({ queryKey: ["my-trips"] });
      qc.invalidateQueries({ queryKey: ["my-conversations"] });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const dateStr = req.trip?.date
    ? new Date(req.trip.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
    : "";

  return (
    <div className="bg-blanc border border-brume rounded-2xl p-4 sm:p-5" data-testid={`request-${req.booking_id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={req.user_name} picture={req.user_picture} size={48} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-ocean">{req.user_name}</span>
            <BookingStatusPill status={req.status} />
          </div>
          <div className="text-sm text-ocean/80 mt-1">
            souhaite rejoindre <Link to={`/sortie/${req.trip_id}`} className="font-semibold text-bretagne hover:text-corail">« {req.trip?.title || "—"} »</Link>
          </div>
          <div className="flex flex-wrap text-xs text-bretagne mt-1 gap-x-3">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{dateStr}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{req.trip?.port}</span>
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{req.seats} place(s)</span>
          </div>
          {req.message && (
            <div className="mt-3 bg-brume/40 border border-brume rounded-xl p-3 text-sm text-ocean/85 leading-relaxed">
              <MessageCircle className="inline w-3.5 h-3.5 text-bretagne mr-1.5" />
              « {req.message} »
            </div>
          )}
          {req.captain_reply && (
            <div className="mt-2 text-xs text-ocean/70 italic">Votre réponse : « {req.captain_reply} »</div>
          )}
        </div>
      </div>

      {req.status === "pending" && (
        <div className="mt-4 space-y-2">
          {showReply && (
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
              placeholder="Un mot pour le participant (facultatif)"
              className="bg-brume/30 border-brume rounded-xl text-sm"
              data-testid={`reply-${req.booking_id}`}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => respond("accept")}
              disabled={busy}
              data-testid={`accept-${req.booking_id}`}
              className="bg-turquoise hover:bg-turquoise/90 text-blanc rounded-full px-5 font-bold tactile"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accepter
            </Button>
            <Button
              onClick={() => respond("refuse")}
              disabled={busy}
              data-testid={`refuse-${req.booking_id}`}
              variant="outline"
              className="border-corail/30 text-corail hover:bg-corail/5 rounded-full px-5 font-bold tactile"
            >
              <XCircle className="w-4 h-4 mr-1.5" /> Refuser
            </Button>
            <button
              onClick={() => setShowReply((v) => !v)}
              className="text-xs text-bretagne hover:text-ocean self-center"
            >
              {showReply ? "Masquer la réponse" : "Ajouter une réponse"}
            </button>
          </div>
        </div>
      )}
      {req.status === "confirmed" && (
        <div className="mt-4">
          <button
            onClick={() => {
              const sp = new URLSearchParams(window.location.search);
              sp.set("tab", "messages");
              window.history.pushState({}, "", `?${sp.toString()}`);
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
            data-testid={`open-message-${req.booking_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-ocean hover:text-corail"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Ouvrir la conversation
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ text, cta, onClick }) {
  return (
    <div className="text-center py-16 bg-sable rounded-3xl border border-brume">
      <Heart className="w-10 h-10 text-ocean mx-auto" />
      <p className="mt-3 text-ocean/80">{text}</p>
      <button onClick={onClick} className="mt-5 bg-ocean text-blanc px-6 py-2.5 rounded-full font-bold tactile">
        {cta}
      </button>
    </div>
  );
}

const CAPTAIN_TABS = [
  { key: "active", label: "Actives" },
  { key: "complete", label: "Complètes" },
  { key: "cancelled", label: "Annulées" },
  { key: "expired", label: "Terminées" },
  { key: "draft", label: "Brouillons" },
];

function CaptainTripsManager({ trips, onPublish }) {
  const [sub, setSub] = useState("active");

  const grouped = useMemo(() => {
    const g = { active: [], complete: [], cancelled: [], expired: [], draft: [] };
    for (const t of trips) {
      const eff = t.effective_status || t.status || "active";
      const bucket = eff === "paused" ? "active" : eff;
      if (g[bucket]) g[bucket].push(t);
    }
    return g;
  }, [trips]);

  const list = grouped[sub] || [];
  const totalActive = grouped.active.length;

  return (
    <div data-testid="captain-trips-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-1.5 bg-brume/60 p-1 rounded-full overflow-x-auto max-w-full scrollbar-hide">
          {CAPTAIN_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`captain-tab-${t.key}`}
              onClick={() => setSub(t.key)}
              className={`text-xs md:text-sm font-bold px-3 md:px-4 py-1.5 rounded-full whitespace-nowrap transition ${
                sub === t.key ? "bg-ocean text-blanc" : "text-ocean/70 hover:text-ocean"
              }`}
            >
              {t.label} ({(grouped[t.key] || []).length})
            </button>
          ))}
        </div>
        <button
          onClick={onPublish}
          data-testid="captain-new-trip"
          className="inline-flex items-center gap-1.5 bg-ocean text-blanc text-sm font-bold px-4 py-2 rounded-full hover:bg-ocean/90 tactile"
        >
          <PlusCircle className="w-4 h-4" /> Nouvelle annonce
        </button>
      </div>

      {sub === "draft" ? (
        <div className="text-center py-16 bg-sable rounded-2xl border border-brume">
          <p className="text-ocean/70">Les brouillons arrivent bientôt — pour l'instant, toutes les annonces sont publiées immédiatement.</p>
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 bg-sable rounded-2xl border border-brume">
          <p className="text-ocean/70">
            {sub === "active" ? "Aucune annonce active. " : ""}
            {sub === "complete" ? "Aucune sortie marquée comme complète." : ""}
            {sub === "cancelled" ? "Aucune annulation, parfait !" : ""}
            {sub === "expired" ? "Pas encore de sorties passées." : ""}
          </p>
          {sub === "active" && totalActive === 0 && (
            <button
              onClick={onPublish}
              className="mt-4 bg-ocean text-blanc px-6 py-2.5 rounded-full font-bold tactile"
            >
              Publier ma première sortie
            </button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((t) => (<CaptainTripCard key={t.trip_id} trip={t} />))}
        </div>
      )}
    </div>
  );
}

function MyBookingCard({ booking }) {
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const t = booking.trip;
  const canCancel = ["pending", "confirmed"].includes(booking.status) && new Date(t.date) > new Date() && t.status !== "cancelled";

  const cancel = async () => {
    try {
      await api.post(`/bookings/${booking.booking_id}/cancel`);
      toast.success("Demande annulée");
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setCancelOpen(false); }
  };

  return (
    <div data-testid={`booking-${booking.booking_id}`} className="bg-blanc border border-brume rounded-3xl p-5 flex flex-col gap-3 hover:shadow-matelot transition">
      <div className="flex items-center justify-between gap-2">
        <BookingStatusPill status={booking.status} />
        {booking.can_review && (
          <span className="inline-flex items-center gap-1 bg-jaune/40 text-ocean text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
            <Star className="w-3 h-3" /> Avis attendu
          </span>
        )}
      </div>
      <Link to={`/sortie/${t.trip_id}`} className="block">
        <h3 className="font-display text-base font-bold text-ocean line-clamp-2 leading-snug hover:text-corail transition">{t.title}</h3>
      </Link>
      <div className="flex flex-wrap text-xs text-bretagne gap-x-3">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{t.port}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(t.date).toLocaleDateString("fr-FR")}</span>
        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{booking.seats} place(s)</span>
      </div>
      {booking.captain_reply && (
        <div className="text-xs bg-brume/40 border border-brume rounded-xl p-2.5 leading-relaxed text-ocean/80">
          <span className="font-semibold">Mot du capitaine :</span> {booking.captain_reply}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-1">
        {canCancel && (
          <Button
            onClick={() => setCancelOpen(true)}
            data-testid={`cancel-booking-${booking.booking_id}`}
            variant="outline"
            className="border-corail/30 text-corail hover:bg-corail/5 rounded-full text-xs font-bold"
            size="sm"
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            {booking.status === "confirmed" ? "Annuler ma participation" : "Annuler ma demande"}
          </Button>
        )}
        {booking.can_review && (
          <Button
            onClick={() => setReviewOpen(true)}
            data-testid={`leave-review-${booking.booking_id}`}
            className="bg-corail hover:bg-corail/90 text-blanc rounded-full text-xs font-bold"
            size="sm"
          >
            <Star className="w-3.5 h-3.5 mr-1" /> Laisser un avis
          </Button>
        )}
        {booking.already_reviewed && (
          <span className="text-xs text-bretagne italic inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Avis déposé
          </span>
        )}
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler votre {booking.status === "confirmed" ? "participation" : "demande"} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le capitaine sera informé immédiatement.{booking.status === "confirmed" ? " La place sera à nouveau disponible." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-no">Non</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancel}
              data-testid="confirm-cancel-yes"
              className="bg-corail hover:bg-corail/90 text-blanc"
            >
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        booking={booking}
        onSubmitted={() => qc.invalidateQueries({ queryKey: ["my-bookings"] })}
      />
    </div>
  );
}

function ReviewDialog({ open, onOpenChange, booking, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", {
        captain_user_id: booking.trip.captain_user_id,
        trip_id: booking.trip.trip_id,
        rating, comment,
      });
      toast.success("Merci pour votre avis !");
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Votre avis sur la sortie</DialogTitle>
          <DialogDescription>
            Comment s'est passée la sortie « {booking.trip?.title} » avec {booking.trip?.captain_name} ?
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold mb-2">Note</div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                data-testid={`star-${n}`}
                className="p-1"
                aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
              >
                <Star className={`w-7 h-7 ${n <= rating ? "fill-jaune text-jaune" : "text-brume"}`} />
              </button>
            ))}
          </div>
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold mb-1.5">Commentaire (facultatif)</div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Partagez votre expérience…"
              data-testid="review-comment"
              className="bg-brume/30 border-brume rounded-xl"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="submit-review"
            className="bg-ocean hover:bg-ocean/90 text-blanc"
          >
            {busy ? "Envoi…" : "Publier mon avis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConversationsView({ conversations }) {
  const [params, setParams] = useSearchParams();
  const initial = params.get("conv");
  const [activeId, setActiveId] = useState(initial);

  useEffect(() => {
    if (activeId) setParams((p) => { p.set("conv", activeId); p.set("tab", "messages"); return p; }, { replace: true });
  }, [activeId, setParams]);

  if (conversations.length === 0) {
    return (
      <div className="text-center py-16 bg-sable rounded-3xl border border-brume">
        <MessageCircle className="w-10 h-10 text-ocean mx-auto" />
        <p className="mt-3 text-ocean/80">Pas encore de conversation.</p>
        <p className="text-xs text-bretagne mt-1">Une conversation est ouverte automatiquement dès qu'un capitaine accepte une demande.</p>
      </div>
    );
  }

  const active = conversations.find((c) => c.conversation_id === activeId);

  return (
    <div className="grid md:grid-cols-3 gap-5">
      <aside className="md:col-span-1 bg-blanc border border-brume rounded-2xl overflow-hidden">
        <ul className="divide-y divide-brume max-h-[600px] overflow-y-auto" data-testid="conversations-list">
          {conversations.map((c) => (
            <li key={c.conversation_id}>
              <button
                onClick={() => setActiveId(c.conversation_id)}
                data-testid={`conv-${c.conversation_id}`}
                className={`w-full text-left px-4 py-3 hover:bg-brume/40 transition flex items-start gap-3 ${
                  activeId === c.conversation_id ? "bg-brume/60" : ""
                }`}
              >
                <Avatar name={c.other?.name} picture={c.other?.picture} size={42} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-ocean text-sm truncate">{c.other?.name || "Utilisateur"}</span>
                    {c.unread > 0 && (
                      <span className="bg-corail text-blanc text-[10px] font-bold px-1.5 rounded-full">{c.unread}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-bretagne truncate">{c.trip?.title}</div>
                  {c.last_message && (
                    <div className="text-xs text-ocean/70 truncate mt-0.5">
                      {c.last_message.from_name === c.other?.name ? "" : "Vous : "}{c.last_message.body}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="md:col-span-2 bg-blanc border border-brume rounded-2xl min-h-[500px]">
        {active ? (
          <ConversationPanel conversationId={active.conversation_id} />
        ) : (
          <div className="h-full grid place-items-center text-bretagne text-sm p-8 text-center">
            Sélectionnez une conversation pour échanger avec le capitaine ou le participant.
          </div>
        )}
      </section>
    </div>
  );
}

function ConversationPanel({ conversationId }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["conv", conversationId],
    queryFn: async () => (await api.get(`/conversations/${conversationId}`)).data,
    refetchInterval: 15000,
  });

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post(`/conversations/${conversationId}/messages`, { body });
      setDraft("");
      await qc.invalidateQueries({ queryKey: ["conv", conversationId] });
      await qc.invalidateQueries({ queryKey: ["my-conversations"] });
      await qc.invalidateQueries({ queryKey: ["notif-count"] });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  if (isLoading || !data) return <div className="p-8 text-center text-bretagne">Chargement…</div>;
  const messages = data.messages || [];

  return (
    <div className="flex flex-col h-full max-h-[600px]">
      <div className="px-5 py-3 border-b border-brume flex items-center gap-3">
        <Avatar name={data.other?.name} picture={data.other?.picture} size={36} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ocean">{data.other?.name}</div>
          {data.trip && (
            <Link to={`/sortie/${data.trip.trip_id}`} className="text-xs text-bretagne hover:text-corail truncate block">
              {data.trip.title}
            </Link>
          )}
        </div>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5" data-testid="messages-list">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-bretagne py-12">
            Démarrez la conversation — un mot d'accueil, une question pratique…
          </div>
        ) : messages.map((m) => {
          const mine = m.from_user_id !== (data.other?.user_id);
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
              <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${
                mine ? "bg-ocean text-blanc rounded-br-sm" : "bg-brume text-ocean rounded-bl-sm"
              }`}>
                {m.body}
                <div className={`text-[10px] mt-1 ${mine ? "text-blanc/70" : "text-bretagne/70"}`}>
                  {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-brume p-3 flex gap-2 items-end">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Votre message…"
          className="flex-1 bg-brume/30 border-brume rounded-xl text-sm resize-none"
          data-testid="msg-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <Button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="msg-send"
          className="bg-ocean hover:bg-ocean/90 text-blanc rounded-full self-stretch px-5"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
